import { themeGet } from '@styled-system/theme-get';
import type { AppearanceProps } from '@trmediaab/zebra';
import {
  appearance,
  BorderBox,
  Box,
  Position,
  styledFactory,
} from '@trmediaab/zebra';
import { Chevron, Triangle } from '@trmediaab/zebra-icons';
import { useTheme } from 'styled-components';
import type {
  BorderProps,
  DisplayProps,
  HeightProps,
  TypographyProps,
  WidthProps,
} from 'styled-system';
import { border, typography } from 'styled-system';

interface SystemSelectProps
  extends BorderProps,
    TypographyProps,
    AppearanceProps {}

const SystemSelect = styledFactory<SystemSelectProps, 'select'>(
  'select',
  'SystemSelect',
  undefined,
  [border, typography, appearance],
);

interface OptionData {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface OptionGroupData {
  label: string;
  data: OptionData[];
}

type OptionOrOptionGroupArray = (OptionData | OptionGroupData)[];

interface IndicatorStyles extends WidthProps {
  iconType?: 'chevron' | 'triangle';
  iconSize?: any; // TODO
  [key: string]: any;
}

// TODO any -> styled system type
type SelectInputProps = DisplayProps &
  WidthProps &
  HeightProps &
  React.ComponentProps<'select'> & {
    data: OptionOrOptionGroupArray;
    color?: React.ComponentProps<typeof Box>['color'];
    pl?: React.ComponentProps<typeof SystemSelect>['pl'];
    selectStyles?: React.ComponentProps<typeof SystemSelect>;
    indicatorStyles?: IndicatorStyles;
  };

const Option = ({ value, label, disabled = false }: OptionData) => (
  <option value={value} disabled={disabled}>
    {label}
  </option>
);

const OptGroup = ({ data, label }: OptionGroupData) => (
  <optgroup label={label}>
    {data.map(option => (
      <Option key={option.label} {...option} />
    ))}
  </optgroup>
);

const ensureSuffix = (value: string, suffix: string) =>
  /\d+$/.test(value) ? value + suffix : value;

/**
 * A component for rendering a custom select input.
 */
const SelectInput = ({
  data,
  display = 'block',
  width,
  height = '40px',
  color = 'text.primary',
  pl = 3,
  selectStyles,
  indicatorStyles,
  ...systemSelectProps
}: SelectInputProps): React.JSX.Element => {
  const theme = useTheme();

  const {
    width: indicatorWidth = 'space.4',
    iconType = 'chevron',
    iconSize = '20px',
    ...indicatorStylesRest
  } = indicatorStyles || {};

  const resolvedIndicatorWidth = ensureSuffix(
    themeGet(`sizes.${indicatorWidth}`, indicatorWidth)({ theme }),
    'px',
  );
  const IndicatorComponent = iconType === 'chevron' ? Chevron : Triangle;

  return (
    <Box display={display} width={width} color={color}>
      <Position.relative display="inline-block" width={width}>
        <SystemSelect
          {...systemSelectProps}
          color="inherit"
          width={width}
          height={height}
          pl={pl}
          pr={resolvedIndicatorWidth}
          appearance="none"
          sx={{
            '&:focus-visible': {
              outline: 'none',
              boxShadow: 'focus',
            },
          }}
          {...selectStyles}
        >
          {data.map(item =>
            'data' in item ? (
              <OptGroup key={item.label} {...(item as OptionGroupData)} />
            ) : (
              <Option key={item.label} {...(item as OptionData)} />
            ),
          )}
        </SystemSelect>
        <BorderBox
          width={indicatorWidth}
          height={height}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderTopRightRadius={selectStyles?.borderRadius}
          borderBottomRightRadius={selectStyles?.borderRadius}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            pointerEvents: 'none',
            opacity: systemSelectProps.disabled ? 0.6 : undefined,
          }}
          {...indicatorStylesRest}
        >
          <IndicatorComponent direction="down" size={iconSize} />
        </BorderBox>
      </Position.relative>
    </Box>
  );
};

export const findOption = (
  data: OptionOrOptionGroupArray,
  searchValue: string | number,
): OptionData | undefined =>
  data
    .flatMap(optionOrOptionGroup =>
      'data' in optionOrOptionGroup
        ? optionOrOptionGroup.data
        : optionOrOptionGroup,
    )
    .find(option => option.value === searchValue);

export default SelectInput;
